import * as React from 'react';
import { DefaultPresenceColorProvider } from '@fluidx/utilities';
import { TokenProvider, PresenceColorProvider, ITelemetryNamespaceLogger } from '@fluidx/office-fluid-types';
import { ComponentUXContext } from './ComponentUXContextProvider';
import { FluidLeap, FluidLeapStyles } from '../leap';
import { defaultLightTheme, setThemingIsEnabled } from '@ms/acui-theme';
import { defaultCoauthorGalleryPalette } from '@ms/acui-collab';
import { ITheme, IStyleFunctionOrObject, styled, concatStyleSetsWithProps } from '@fluentui/react';

export interface LeapPartProps {
  presenceColorProvider?: PresenceColorProvider;

  tokenProvider?: TokenProvider;

  namespaceLogger?: ITelemetryNamespaceLogger;

  styles?: IStyleFunctionOrObject<LeapPartStyleProps, LeapPartStyles>;

  theme?: ITheme;
}

interface LeapPartStyles {
  fluidLeap?: FluidLeapStyles;
}

type LeapPartStyleProps = Pick<LeapPartProps, 'theme'>;

export const leapPartTestId = 'leap';

/**
 * Leap stands for Last Edited and Presence.
 *
 * This control acts as a switch between the CoauthorGallery and the LastEdited.
 * The CoauthorGallery will be displayed when there are multiple coauthors in the same document,
 * otherwise it will show the LastEdited.
 *
 * It is used in the Component UX Chrome.
 */
export const LeapPart = React.memo(
  styled((props: LeapPartProps) => {
    const { presenceColorProvider, tokenProvider, namespaceLogger, styles } = props;
    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { container, component, stringTable, localization } = React.useContext(ComponentUXContext)!;

    const resolvedStyles = concatStyleSetsWithProps(props, styles);

    return container ? (
      <FluidLeap
        data-testid={leapPartTestId}
        container={container}
        presenceColorProvider={presenceColorProvider ? presenceColorProvider : new DefaultPresenceColorProvider()}
        pageLogger={namespaceLogger}
        tokenProvider={tokenProvider}
        maxFacepileCoauthors={4} // Maximum 4 coauthor coins shown per redlines
        styles={resolvedStyles.fluidLeap}
        lastEditedTracker={component?.IComponentLastEditedTracker}
        presenceAwayStatusTrackerPromise={component?.PresenceAwayStatusTrackerPromise}
        // Localization
        localization={localization}
        stringTable={stringTable}
      />
    ) : null;
  }, getFluidLeapStyles)
);

function getFluidLeapStyles(props: LeapPartStyleProps): LeapPartStyles {
  // If there is no theme we will default to the light theme
  const { theme = defaultLightTheme } = props;

  // Ensure the flight for theming in acui-collab is on
  setThemingIsEnabled(true);

  // Get the palette based off of the theme and set our needed additions
  const coauthGalleryPalette = defaultCoauthorGalleryPalette(theme);
  const ringWidth = coauthGalleryPalette.ringWidth;
  const ringPadding = coauthGalleryPalette.ringPadding;
  const ribbonCoinSize = 20;
  const ribbonCoinSizeFull = ribbonCoinSize + 2 * (ringPadding + ringWidth);
  const memberPadding = 2;

  return {
    fluidLeap: {
      coauthorGallery: {
        palette: {
          ...coauthGalleryPalette,
          // Coin Constants
          ringWidth,
          ringPadding,
          ribbonCoinSize,
          ribbonCoinSizeFull,
          // Facepile Constants
          memberPadding,
          memberSize: 2 * memberPadding + ribbonCoinSizeFull
        },
        root: {} // Root style is used to get the coauthor gallery display centered in its layout rect
      }
    }
  };
}
